/**
 * 실물 표 한 벌 — 재고 현황(work)과 제품 상세(read)가 같은 표를 쓴다(현민 2026-10-14. VOCABULARY "실물 표 한 벌").
 * 실물 한 장 = 한 줄. PN 으로 묶지 않는다 — 묶음은 품목 합산 뷰의 몫. 서버 줄은 "안에 n장".
 * 2026-10-14 InventoryView 에서 이동(renderUnitRow · STATUS_WORD · ORIGIN_WORD · statusChip · costCell · SCRAP_CONDITION).
 */

import type { EStockStatus, EUnitOrigin, UnitView, EInventoryEventReason, EInventoryEventType } from '../api/client';
import { escapeHtml } from '../lib/cells';
import { costTextOf } from '../lib/money';
import { ROW_NO_HEAD, rowNoCell } from './row-no';

/**
 * 실물 표의 꼴 — 열은 셋 다 같다(2026-11-03 다시 — "체크칸이 바로 켜져 있으면 안 된다").
 * browse 재고 현황 평소(체크 칸 없음 · 줄 누르면 실물 상세 새 탭) · pick 재고 현황 고르기 모드([작업] 뒤 — 체크 칸 · 줄 누르면 체크) ·
 * read 제품 · 재고 · 실물 상세(체크 칸 없음 · #번호 링크)
 */
export type UnitTableLook =
  | { kind: 'browse' }
  | { kind: 'pick'; checked: ReadonlySet<string> }   // 체크된 실물 id
  | { kind: 'read' };

export const STATUS_WORD: Record<EStockStatus, string> = { inspecting: '검수 중', available: '가용', reserved: '세팅됨', dispatched: '출고됨', rented: '임대', asset: '자산', retired: 'retired' };   // 분실 · 파손 · 반품 · 폐기를 묶은 것 — 우리말 '이탈' 이 낯설어 영어 그대로(현민 2026-10-19)
export const ORIGIN_WORD: Record<EUnitOrigin, string> = { purchased: '구매', legacy: '옛 재고', disassembled: '분해', assembled: '조립', found: '실사 발견' };
const SCRAP_CONDITION = 'scrap';
const COLUMN_COUNT = 13;   // 체크 칸 빼고 — 순번 · 파트넘버 · 품목 · 시리얼 · 상태 · 컨디션 · 카테고리 · 창고 · 출처 · 원가 · 묶인 곳 · 안에 · 실물

const statusChip = (status: EStockStatus): string => `<span class="chip pv-st-${status}">${STATUS_WORD[status]}</span>`;
/** 실물 한 장의 상태 칩 — 자산이면 메모를 호버로(2026-10-29) */
const statusChipOf = (unit: UnitView): string => (unit.asset_note === null ? statusChip(unit.stock_status) : `<span class="chip pv-st-${unit.stock_status}" title="${escapeHtml(unit.asset_note)}">${STATUS_WORD[unit.stock_status]}</span>`);
/** 파트넘버 칸의 글 — 없으면 제품번호(PN 없는 제품 · SKU 모르는 서버). 제품번호 칸이 없는 표(실물 · 구매 건)가 같이 쓴다(현민 2026-10-19) */
/** 이벤트 종류 · 사유의 화면 말 — 실물 상세의 이력과 변동표가 같이 쓴다(2026-10-20 UnitDetailView 에서 옮김) */
export const EVENT_WORD: Record<EInventoryEventType, string> = { created: '입고', inspected: '검수 완료', transferred: '이동', reserved: '세팅됨', dispatched: '출고', rented: '임대', asset: '자산으로', returned: '회수 · 반품', retired: '이탈', changed: '값 바뀜' };
export const REASON_WORD: Record<EInventoryEventReason, string> = { stocktake: '실사', regraded: '컨디션 재판정', typo: '오타', cost: '원가 입력', assembled: '서버에 넣음', disassembled: '서버에서 뺌', merged: '제품 합침', hold: '홀딩', etc: '기타' };

export function pnTextOf(row: { option_part: string | null; hclt_number: string }): string {
  return row.option_part ?? row.hclt_number;
}

const costCell = (unit: UnitView): string => costTextOf(unit.cost_amount, unit.cost_currency);

/** 실물 표 전부 — 머리글 + 줄. rows 가 비면 emptyWord 한 줄 */
export function renderUnitTable(rows: readonly UnitView[], look: UnitTableLook, emptyWord: string, offset: number): string {
  const checkHead = look.kind === 'pick' ? '<th></th>' : '';
  const colspan = look.kind === 'pick' ? COLUMN_COUNT + 1 : COLUMN_COUNT;
  return `
    <table class="pv-table pv-units">
      <thead><tr>${checkHead}${ROW_NO_HEAD}<th>번호</th><th>파트넘버</th><th>품목</th><th>시리얼</th><th>상태</th><th>컨디션</th><th>카테고리</th><th>창고</th><th>출처</th><th>원가</th><th>묶인 곳</th><th></th></tr></thead>
      <tbody>${rows.map((unit, index) => renderUnitRow(unit, look, offset + index)).join('') || `<tr><td colspan="${colspan}" class="text-muted">${emptyWord}</td></tr>`}</tbody>
    </table>`;
}

/** 줄 전체가 누르는 자리인가 — browse 는 실물 상세(unit-row), pick 은 체크(check). read 는 없음 */
function rowAttrsOf(unit: UnitView, look: UnitTableLook): string {
  switch (look.kind) {
    case 'browse': return ` data-action="unit-row" data-id="${unit.id}" title="실물 상세(새 탭)"`;
    case 'pick':   return ` data-action="check" data-id="${unit.id}"`;
    case 'read':   return '';
    default: throw new Error(`unhandled switch case: ${look satisfies never}`);
  }
}

/** 실물 한 줄. pick 이면 체크 칸, read 면 #번호가 실물 상세 링크(browse · pick 은 줄이 누르는 자리라 글) */
function renderUnitRow(unit: UnitView, look: UnitTableLook, no: number): string {
  const isChecked = look.kind === 'pick' && look.checked.has(unit.id);
  const pn = escapeHtml(pnTextOf(unit));
  const checkCell = look.kind === 'pick' ? `<td><input type="checkbox" data-action="check" data-id="${unit.id}" ${isChecked ? 'checked' : ''}></td>` : '';
  const pnCell = `<span class="mono"><b>${pn}</b></span>`;
  return `
    <tr class="${isChecked ? 'pv-checked ' : ''}${look.kind === 'read' ? '' : 'pl-row'}"${rowAttrsOf(unit, look)}>
      ${checkCell}${rowNoCell(no, 0)}
      <td class="pl-sub pl-id mono">${look.kind === 'read' ? `<a data-link href="/inventory/units/${unit.id}" target="_blank" rel="noopener" title="실물 상세">#${unit.id}</a>` : `#${unit.id}`}</td>
      <td class="pl-main">${pnCell}</td>
      <td class="pl-main pl-desc"><div class="pl-clamp">${unit.description === null ? '' : escapeHtml(unit.description)}</div></td>
      <td class="pl-main mono">${unit.serial_number === null ? '<span class="text-muted">없음</span>' : escapeHtml(unit.serial_number)}</td>
      <td class="pl-main">${statusChipOf(unit)}${unit.is_held ? ` <span class="chip pv-hold" title="${escapeHtml(unit.hold_note === null ? '' : unit.hold_note)}">홀딩</span>` : ''}</td>
      <td class="pl-sub">${unit.condition === SCRAP_CONDITION ? '<span class="chip pv-scrap">scrap</span>' : escapeHtml(unit.condition)}</td>
      <td class="pl-sub">${unit.spec_category === null ? '—' : escapeHtml(unit.spec_category)}</td>
      <td class="pl-sub">${escapeHtml(unit.warehouse)}</td>
      <td class="pl-sub">${ORIGIN_WORD[unit.origin]}</td>
      <td class="pl-sub mono">${costCell(unit)}</td>
      <td class="pl-sub">${unit.quote_no === null ? '' : `견적 ${escapeHtml(unit.quote_no)}`}</td>
      <td class="pl-sub">${unit.child_count > 0 ? `<span class="chip chip-primary">안에 ${unit.child_count}장</span>` : ''}</td>
    </tr>`;
}
